import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import { ImageCard } from '@sb1/ffe-cards-react';
import { getImageUrl } from '../../utils/cms-utils';
import styles from './carousel.module.less';

const getSlidesPerView = (width) => {
    if (width <= 450) {
        return 1.3;
    }
    if (width <= 761) {
        return 1.73;
    }
    if (width <= 1000) {
        return 3.2;
    }
    return 4;
};

const HighlightCard = ({ articleCard }) => {
    const { title, introduction, image, link } = articleCard?.fields || {};

    if (!articleCard?.fields) {
        return null;
    }

    const imageSrc = getImageUrl(image);
    const imageAltText = image?.fields?.description;
    const url = link?.fields?.url;

    return (
        <ImageCard imageSrc={imageSrc} imageAltText={imageAltText} noMargin className={styles.highlightCard}>
            {({ CardAction, Title, Text }) => (
                <div className={styles.highlightCardContent}>
                    <Title>
                        {url ? (
                            <CardAction as="a" href={url} target="_blank" rel="noopener noreferrer">
                                {title}
                            </CardAction>
                        ) : (
                            title
                        )}
                    </Title>
                    <Text>{introduction}</Text>
                </div>
            )}
        </ImageCard>
    );
};

const CarouselDots = ({ count, selectedIndex }) => {
    if (count <= 1) {
        return null;
    }

    const svgWidth = 12.5 + 20 * (count - 1);

    return (
        <div className={styles.dots} aria-hidden="true">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={svgWidth}
                height="12"
                viewBox={`0 0 ${svgWidth} 12`}
                fill="none"
            >
                {Array.from({ length: count }, (_, index) => (
                    <circle
                        key={index}
                        cx={6.5 + 20 * index}
                        cy="6"
                        r="6"
                        fill={selectedIndex === index ? '#002776' : '#ADADAD'}
                    />
                ))}
            </svg>
        </div>
    );
};

const Carousel = ({ cards }) => {
    const [viewportWidth, setViewportWidth] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth : 1200
    );
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        const updateViewportWidth = () => setViewportWidth(window.innerWidth);

        window.addEventListener('resize', updateViewportWidth);

        return () => window.removeEventListener('resize', updateViewportWidth);
    }, []);

    const slidesPerView = getSlidesPerView(viewportWidth);
    const canScroll = cards.length > slidesPerView;

    const slides = useMemo(() => {
        if (!canScroll) {
            return cards;
        }

        return [...cards, ...cards, ...cards];
    }, [canScroll, cards]);

    const watchDrag = useCallback(() => cards.length > getSlidesPerView(window.innerWidth), [cards.length]);

    const emblaOptions = useMemo(
        () => ({
            align: 'start',
            containScroll: canScroll ? false : 'trimSnaps',
            loop: canScroll,
            slidesToScroll: 1,
            watchDrag,
        }),
        [canScroll, watchDrag]
    );

    const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);

    const updateSelectedIndex = useCallback(
        (api) => {
            setSelectedIndex(api.selectedScrollSnap() % cards.length);
        },
        [cards.length]
    );

    useEffect(() => {
        if (!emblaApi) {
            return undefined;
        }

        updateSelectedIndex(emblaApi);

        emblaApi.on('reInit', updateSelectedIndex);
        emblaApi.on('select', updateSelectedIndex);

        return () => {
            emblaApi.off('reInit', updateSelectedIndex);
            emblaApi.off('select', updateSelectedIndex);
        };
    }, [emblaApi, updateSelectedIndex]);

    return (
        <div className={styles.carouselWrapper}>
            <div
                className={classNames(
                    styles.carouselViewport,
                    canScroll && styles.carouselViewportScrollable,
                    !canScroll && styles.carouselViewportStatic
                )}
                ref={emblaRef}
            >
                <div className={styles.carouselContainer}>
                    {slides.map((articleCard, index) => (
                        <div key={`${articleCard.sys.id}-${index}`} className={styles.carouselSlide}>
                            <HighlightCard articleCard={articleCard} />
                        </div>
                    ))}
                </div>
            </div>
            {canScroll && <CarouselDots count={cards.length} selectedIndex={selectedIndex} />}
        </div>
    );
};

export default Carousel;
